/**
 * Markdown renderer for task descriptions and comments (FRIDAY-5).
 *
 * react-markdown never interprets raw HTML (no rehype-raw), and its default
 * urlTransform strips unsafe protocols like `javascript:`, so user content
 * can't inject markup or script. GFM adds tables, task lists, strikethrough
 * and autolinks.
 */

import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'

const components: Components = {
  p: ({ node: _n, ...props }) => <p className="my-1.5 first:mt-0 last:mb-0" {...props} />,
  h1: ({ node: _n, ...props }) => <h1 className="mb-1.5 mt-3 text-base font-semibold first:mt-0" {...props} />,
  h2: ({ node: _n, ...props }) => <h2 className="mb-1.5 mt-3 text-sm font-semibold first:mt-0" {...props} />,
  h3: ({ node: _n, ...props }) => <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0" {...props} />,
  ul: ({ node: _n, ...props }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5" {...props} />,
  ol: ({ node: _n, ...props }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5" {...props} />,
  li: ({ node: _n, className, ...props }) => (
    // GFM task-list items carry `task-list-item`; drop the bullet for those.
    // The old `-ml-5` pulled the checkbox outside the comment bubble (the
    // bubble's padding is smaller than the bullet indent), so the checkbox
    // rendered out of the box. The task item is laid out as a flex row
    // instead: the checkbox sits inside the bubble and the text wraps next
    // to it.
    <li
      className={cn(
        className?.includes('task-list-item') &&
          'flex list-none items-start gap-1.5 break-words pl-0',
      )}
      {...props}
    />
  ),
  // GFM task lists render a checkbox; keep it inside the bubble and aligned
  // with the first line of the item. `readOnly` keeps React from warning
  // about a checked input without an onChange handler.
  input: ({ node: _n, ...props }) => (
    <input
      {...props}
      readOnly
      className="mt-[0.2rem] h-3.5 w-3.5 shrink-0 rounded accent-brand-500"
    />
  ),
  a: ({ node: _n, ...props }) => (
    <a
      {...props}
      target="_blank"
      rel="noopener noreferrer"
      // The description preview opens the editor on click — let links win.
      onClick={(e) => e.stopPropagation()}
      className="text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-300"
    />
  ),
  blockquote: ({ node: _n, ...props }) => (
    <blockquote className="my-1.5 border-l-2 border-surface-300 pl-3 text-surface-500 dark:border-surface-600" {...props} />
  ),
  code: ({ node: _n, className, ...props }) => (
    <code
      className={cn(
        'font-mono text-[0.85em]',
        // Fenced blocks get a language-* class; inline code gets a pill.
        !className && 'rounded bg-surface-100 px-1 py-0.5 dark:bg-surface-800',
        className,
      )}
      {...props}
    />
  ),
  pre: ({ node: _n, ...props }) => (
    <pre className="my-1.5 max-w-full overflow-x-auto rounded-md bg-surface-100 p-2 dark:bg-surface-800" {...props} />
  ),
  hr: ({ node: _n, ...props }) => <hr className="my-3 border-surface-200 dark:border-surface-700" {...props} />,
  table: ({ node: _n, ...props }) => (
    // The table styles live in `styles/index.css` (`.friday-table`) so the
    // page editor and this read path share one stylesheet. The wrapper
    // keeps a wide table scrolling inside the drawer instead of widening
    // the page.
    <div className="friday-table-wrap my-1.5">
      <table className="friday-table" {...props} />
    </div>
  ),
  img: ({ node: _n, ...props }) => <img className="my-1.5 max-w-full rounded" loading="lazy" {...props} />,
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  return (
    // `min-w-0 max-w-full` keeps every block inside the box that hosts the
    // body (the comment bubble, the description preview), so a long word, a
    // code block or a table can never bleed out of it.
    <div className={cn('min-w-0 max-w-full break-words text-sm leading-relaxed', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  )
}
